import type { ProgressData, StageClear } from "../store/progress";

/**
 * Merge a remote progress snapshot into the local one (spec §12: local is
 * source of truth; stardust/stars merge by max). Never loses progress.
 */
export function mergeProgress(local: ProgressData, remote: ProgressData): ProgressData {
  // Adopt the account level/XP from whichever side is further along.
  const localRank = local.accountLevel * 1e6 + local.accountXp;
  const remoteRank = remote.accountLevel * 1e6 + remote.accountXp;
  const account = remoteRank > localRank ? remote : local;

  // Per-cols clears: union by max for each (stageId, cols) pair. Legacy flat
  // records on either side get folded into the 4-cols slot first.
  const clears: Record<string, Record<number, StageClear>> = {};
  const ingest = (src: ProgressData["clears"] | undefined): void => {
    if (!src) return;
    for (const [id, rec] of Object.entries(src)) {
      const perCols: Record<number, StageClear> =
        rec && typeof rec === "object" && "stars" in (rec as object)
          ? { 4: rec as unknown as StageClear } // legacy flat shape
          : (rec as unknown as Record<number, StageClear>);
      const target = clears[id] ?? (clears[id] = {});
      for (const [k, v] of Object.entries(perCols)) {
        const cols = Number(k);
        const cur = target[cols];
        target[cols] = cur
          ? {
              stars: Math.max(cur.stars, v.stars),
              bestScore: Math.max(cur.bestScore, v.bestScore),
              bestDrops: Math.min(cur.bestDrops, v.bestDrops),
            }
          : v;
      }
    }
  };
  ingest(local.clears);
  ingest(remote.clears);

  const skills: Record<string, number> = { ...local.skills };
  for (const [id, lvl] of Object.entries(remote.skills)) {
    skills[id] = Math.max(skills[id] ?? 0, lvl);
  }

  // Daily missions are day-scoped — keep the side whose day is newer (or, on the
  // same day, the one with more accumulated progress).
  const localDay = local.missionDay ?? "";
  const remoteDay = remote.missionDay ?? "";
  const missionSide =
    localDay === remoteDay
      ? sumValues(local.missionProgress) >= sumValues(remote.missionProgress)
        ? local
        : remote
      : localDay > remoteDay
        ? local
        : remote;

  // Best endless score per width — take the max on each side.
  const bestEndlessByCols: Record<number, number> = { ...(local.bestEndlessByCols ?? {}) };
  for (const [w, v] of Object.entries(remote.bestEndlessByCols ?? {})) {
    const k = Number(w);
    bestEndlessByCols[k] = Math.max(bestEndlessByCols[k] ?? 0, v);
  }

  // Union the per-stage cleared widths (gates the column picker).
  const clearCols: Record<string, number[]> = {};
  for (const [id, widths] of [
    ...Object.entries(local.clearCols ?? {}),
    ...Object.entries(remote.clearCols ?? {}),
  ]) {
    const set = new Set([...(clearCols[id] ?? []), ...widths]);
    clearCols[id] = [...set].sort((a, b) => a - b);
  }

  return {
    accountLevel: account.accountLevel,
    accountXp: account.accountXp,
    skills,
    stardust: Math.max(local.stardust, remote.stardust),
    maxTierReached: Math.max(local.maxTierReached, remote.maxTierReached),
    clears,
    clearCols,
    unlocks: [...new Set([...local.unlocks, ...remote.unlocks])],
    bestEndless: Math.max(local.bestEndless, remote.bestEndless),
    bestEndlessByCols,
    achievements: [...new Set([...local.achievements, ...remote.achievements])],
    dailyLastDate: local.dailyLastDate >= remote.dailyLastDate ? local.dailyLastDate : remote.dailyLastDate,
    dailyStreak: Math.max(local.dailyStreak, remote.dailyStreak),
    dailyBest: mergeDailyBest(local.dailyBest, remote.dailyBest),
    missionDay: missionSide.missionDay ?? "",
    missionProgress: { ...(missionSide.missionProgress ?? {}) },
    missionCols: missionSide.missionCols ?? 4,
    missionClaimed: [...new Set(missionSide.missionClaimed ?? [])],
    loginLastDate:
      (local.loginLastDate ?? "") >= (remote.loginLastDate ?? "")
        ? local.loginLastDate ?? ""
        : remote.loginLastDate ?? "",
    loginStreak: Math.max(local.loginStreak ?? 0, remote.loginStreak ?? 0),
    missionsClaimedTotal: Math.max(local.missionsClaimedTotal ?? 0, remote.missionsClaimedTotal ?? 0),
    seenWild: Boolean(local.seenWild || remote.seenWild),
    seenBomb: Boolean(local.seenBomb || remote.seenBomb),
    seenBoss: Boolean(local.seenBoss || remote.seenBoss),
    ownedCosmetics: [...new Set([...(local.ownedCosmetics ?? []), ...(remote.ownedCosmetics ?? [])])],
    tutorialDone: Boolean(local.tutorialDone || remote.tutorialDone),
    updatedAt: Math.max(local.updatedAt, remote.updatedAt),
  };
}

function sumValues(rec: Record<string, number> | undefined): number {
  if (!rec) return 0;
  let s = 0;
  for (const v of Object.values(rec)) s += v;
  return s;
}

function mergeDailyBest(
  a: Record<string, number>,
  b: Record<string, number>,
): Record<string, number> {
  const out: Record<string, number> = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = Math.max(out[k] ?? 0, v);
  return out;
}
